import { BodyText, Button, Flex, Stack } from "@knowtex-ai/components";
import type { ReactElement } from "react";
import {
  getStartRecordingLabel,
  isStartRecordingDisabled,
  shouldShowConsentMessage,
  shouldShowReturnHome,
  shouldShowStartRecording,
  type RecordingStatus,
} from "./recorderUiRules";

export interface RecorderIdleActionsProps {
  permission: boolean;
  recordingStatus: RecordingStatus;
  hasRecorded: boolean;
  isMergingChunks: boolean;
  isAudio: boolean;
  isStartingRecording: boolean;
  isAudioTestStarting: boolean;
  isAudioTestActive: boolean;
  org?: string | null;
  onStartRecording: () => void;
  onReturnHome: () => void;
}

export default function RecorderIdleActions({
  permission,
  recordingStatus,
  hasRecorded,
  isMergingChunks,
  isAudio,
  isStartingRecording,
  isAudioTestStarting,
  isAudioTestActive,
  org,
  onStartRecording,
  onReturnHome,
}: RecorderIdleActionsProps): ReactElement | null {
  const showStart = shouldShowStartRecording({
    permission,
    recordingStatus,
    hasRecorded,
    isMergingChunks,
  });
  const showReturn = shouldShowReturnHome({
    recordingStatus,
    hasRecorded,
    isMergingChunks,
    isAudio,
    isStartingRecording,
  });
  const showConsent = shouldShowConsentMessage({ org, recordingStatus, hasRecorded });
  const startDisabled = isStartRecordingDisabled({
    isStartingRecording,
    isAudioTestStarting,
    isAudioTestActive,
  });

  if (!showStart && !showReturn) {
    return null;
  }

  return (
    <Stack gap={12} className="w-full">
      {showStart ? (
        <Stack gap={8} className="w-full">
          <Button
            id="start_recording"
            type="button"
            variant="primary-alt"
            fullWidth
            disabled={startDisabled}
            onClick={onStartRecording}
          >
            {getStartRecordingLabel(isStartingRecording)}
          </Button>
          {showConsent ? (
            <BodyText size="small" muted className="text-center">
              By clicking record, you confirm that if a patient or surrogate is present, you have
              obtained their verbal consent to have the conversation recorded and summarized by the
              AI software.
            </BodyText>
          ) : null}
        </Stack>
      ) : null}

      {showReturn ? (
        <Flex justifyContent="center">
          <Button id="return_home" type="button" variant="text" onClick={onReturnHome}>
            Return Home
          </Button>
        </Flex>
      ) : null}
    </Stack>
  );
}
